// Hidden "operator mode" toggle. Operator-only affordances (Open repo, Open in
// ArgoCD, GitOps commit links, …) are gated behind this so a client looking at
// the dashboard doesn't see operator plumbing. It's deliberately undiscoverable:
// type the sequence `optools` anywhere (outside a text field), or press
// Ctrl+Alt+O, to toggle. The choice persists in a cookie for a year, so once an
// operator unlocks it on their machine it stays until they lock it again.

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useToast } from '../components/ui';

const COOKIE = 'stf_optools';
// Type this (lowercase) anywhere outside an input to flip the mode. Keep it
// short, unguessable-ish, and unlikely to occur in normal typing.
const UNLOCK_SEQUENCE = 'optools';

function readCookie(name: string): boolean {
  return document.cookie.split('; ').includes(`${name}=1`);
}
function writeCookie(name: string, on: boolean) {
  // 1-year persistence when on; max-age=0 deletes it when off.
  document.cookie = `${name}=1; path=/; samesite=lax; ${on ? 'max-age=31536000' : 'max-age=0'}`;
}

interface OperatorMode {
  unlocked: boolean;
  toggle: () => void;
}

const Ctx = createContext<OperatorMode>({ unlocked: false, toggle: () => {} });

export function OperatorModeProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [unlocked, setUnlocked] = useState<boolean>(() => readCookie(COOKIE));

  const toggle = useCallback(() => {
    setUnlocked((prev) => {
      const next = !prev;
      writeCookie(COOKIE, next);
      toast?.info(
        next ? 'Operator tools unlocked' : 'Operator tools hidden',
        next ? 'ArgoCD, repo and GitOps links are now visible.' : 'The dashboard is back in client view.',
      );
      return next;
    });
  }, [toast]);

  // Global key listener: the Ctrl+Alt+O shortcut, plus the typed unlock
  // sequence. Both are ignored while focus is in a text field so they never
  // interfere with normal typing.
  useEffect(() => {
    let buf = '';
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.ctrlKey && e.altKey && (e.key === 'o' || e.key === 'O')) {
        e.preventDefault();
        buf = '';
        toggle();
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        buf = (buf + e.key.toLowerCase()).slice(-UNLOCK_SEQUENCE.length);
        if (buf === UNLOCK_SEQUENCE) {
          buf = '';
          toggle();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return <Ctx.Provider value={{ unlocked, toggle }}>{children}</Ctx.Provider>;
}

export function useOperatorMode(): OperatorMode {
  return useContext(Ctx);
}
